import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject, signal } from '@angular/core';

import { ToastContainerComponent } from '@shared/components/toast/toast-container.component';
import { TOAST_STATE, ToastItem, ToastState, ToastType } from '@shared/components/toast/toast-item';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private overlayRef: OverlayRef | null = null;
  private readonly _toasts = signal<ToastItem[]>([]);
  private nextId = 0;

  private remove(id: number): void {
    this._toasts.update(list => list.filter(t => t.id !== id));
  }

  private ensureContainer(): void {
    if (this.overlayRef) return;

    const state: ToastState = {
      toasts: this._toasts.asReadonly(),
      remove: (id: number) => this.remove(id)
    };

    const stateInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: TOAST_STATE, useValue: state }]
    });

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().top('16px').right('16px'),
      hasBackdrop: false
    });

    this.overlayRef.attach(new ComponentPortal(ToastContainerComponent, null, stateInjector));
  }

  show(type: ToastType, title: string, message = '', ms = 3000): void {
    this.ensureContainer();
    const item: ToastItem = { id: ++this.nextId, type, title, message, duration: ms };
    this._toasts.update(list => [...list, item]);
    setTimeout(() => this.remove(item.id), ms);
  }

  success(title: string, message = ''): void {
    this.show('success', title, message);
  }

  error(title: string, message = ''): void {
    this.show('error', title, message);
  }

  /** Show each message as its own separate toast. */
  errorList(messages: string[], ms = 3000): void {
    messages.forEach((msg, i) => {
      setTimeout(() => this.show('error', msg, '', ms), i * 80);
    });
  }

  warning(title: string, message = ''): void {
    this.show('warning', title, message);
  }

  info(title: string, message = ''): void {
    this.show('info', title, message);
  }

  dismiss(): void {
    this._toasts.set([]);
  }
}
